import { useCallback } from 'react'
import { Server, Wifi, WifiOff, Copy, AlertTriangle } from 'lucide-react'
import { cn, truncateAddress, formatRelativeTime } from '@/lib/utils'
import { useToast } from '@/hooks/useToast'
import { useMarketDataHealth, MARKET_DATA_BASE_URL } from '@/hooks/useMarketDataHealth'
import { useMarketMakerHealth, MARKET_MAKER_BASE_URL } from '@/hooks/useMarketMakerHealth'
import { useSeedbotHealth, SEEDBOT_BASE_URL } from '@/hooks/useSeedbotHealth'
import { ServiceHealthCard, type ServiceStatus, type SubMetric } from './shared/ServiceHealthCard'
import { RealTimeIndicator } from './shared/RealTimeIndicator'

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

function fmtUptime(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '—'
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

function fmtRelativeOrNever(iso: string | null | undefined): string {
  if (!iso) return 'never'
  return `${formatRelativeTime(iso)} ago`
}

// TODO: useSeedbotHealth's typed shape declares scheduler.launchesPerHour but
// the actual /health response returns scheduler.launchesPerCycle + intervalMinutes.
// Reading the real field via a defensive cast here so we don't have to modify
// the hook (per impl-pack constraint). Future cleanup: regenerate the
// SeedbotHealthData interface from the live response shape.
function getSeedbotCadence(scheduler: { running: boolean; launchesPerHour?: number }): string {
  const cast = scheduler as { launchesPerCycle?: number; intervalMinutes?: number; launchesPerHour?: number }
  const cadence = cast.launchesPerCycle ?? cast.launchesPerHour
  const interval = cast.intervalMinutes
  if (cadence === undefined) return '—'
  if (interval !== undefined) return `${cadence} every ${interval}m`
  return `${cadence}/hr`
}

function mapFetchError(error: unknown): string {
  if (!error) return 'Service is unreachable.'
  const msg = error instanceof Error ? error.message : String(error)
  if (/network|fetch|failed to fetch/i.test(msg)) return 'Unable to reach service. Check connectivity.'
  if (/Health endpoint returned 5\d\d/.test(msg)) return 'Service is currently degraded.'
  if (/Health endpoint returned 4\d\d/.test(msg)) return 'Service rejected the request.'
  return 'Service is currently unreachable.'
}

// ────────────────────────────────────────────────────────────────────────
// Sub-views
// ────────────────────────────────────────────────────────────────────────

function MarketDataGroup() {
  const { data, isLoading, isError, error, refetch, isFetching } = useMarketDataHealth()

  if (isError || isLoading) {
    return (
      <ServiceHealthCard
        title="market-data"
        url={MARKET_DATA_BASE_URL}
        status={isError ? 'error' : 'unknown'}
        isLoading={isLoading}
        isError={isError}
        errorMessage={mapFetchError(error)}
        onRetry={() => refetch()}
        isRetrying={isFetching}
      />
    )
  }

  if (!data) return null
  const status: ServiceStatus = data.status === 'ok' ? 'ok' : 'degraded'
  const subMetrics: SubMetric[] = [
    { label: 'Subscriptions', value: data.subscriptions.toLocaleString() },
    { label: 'Global subscribers', value: data.globalSubscribers.toLocaleString() },
  ]

  return (
    <ServiceHealthCard
      title="market-data"
      url={MARKET_DATA_BASE_URL}
      status={status}
      statusLabel={data.status}
      subMetrics={subMetrics}
    />
  )
}

function MarketMakerGroup() {
  const { data, isLoading, isError, error, refetch, isFetching } = useMarketMakerHealth()

  if (isError || isLoading) {
    return (
      <ServiceHealthCard
        title="market-maker"
        url={MARKET_MAKER_BASE_URL}
        status={isError ? 'error' : 'unknown'}
        isLoading={isLoading}
        isError={isError}
        errorMessage={mapFetchError(error)}
        onRetry={() => refetch()}
        isRetrying={isFetching}
      />
    )
  }

  if (!data) return null

  // status=ok + websocket disconnected = degraded (cluster needs WS for prices)
  let status: ServiceStatus
  let statusLabel: string
  if (data.status !== 'ok') {
    status = 'degraded'
    statusLabel = data.status
  } else if (!data.websocket_connected) {
    status = 'degraded'
    statusLabel = 'ws-disconnected'
  } else {
    status = 'ok'
    statusLabel = 'ok'
  }

  const subMetrics: SubMetric[] = [
    { label: 'Uptime', value: fmtUptime(data.uptime_seconds) },
    {
      label: 'Websocket',
      value: data.websocket_connected ? (
        <span className="inline-flex items-center gap-1 text-status-ok">
          <Wifi className="h-3.5 w-3.5" /> connected
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-status-error">
          <WifiOff className="h-3.5 w-3.5" /> disconnected
        </span>
      ),
    },
    { label: 'Active configs', value: data.active_configs.toLocaleString() },
    { label: 'Trades executed', value: data.total_trades_executed.toLocaleString() },
    { label: 'Last trade', value: fmtRelativeOrNever(data.last_trade_at), hint: data.last_trade_at ?? undefined },
    { label: 'Active trades', value: data.active_trades.toLocaleString() },
  ]

  return (
    <ServiceHealthCard
      title="market-maker"
      url={MARKET_MAKER_BASE_URL}
      status={status}
      statusLabel={statusLabel}
      subMetrics={subMetrics}
    />
  )
}

function SeedbotGroup() {
  const { data, isLoading, isError, error, refetch, isFetching } = useSeedbotHealth()
  const { toast } = useToast()

  const copy = useCallback(
    async (text: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text)
        toast({ title: `Copied ${label}`, description: text })
      } catch {
        toast({ title: 'Copy failed', variant: 'destructive' })
      }
    },
    [toast]
  )

  if (isError || isLoading) {
    return (
      <ServiceHealthCard
        title="seedbot"
        url={SEEDBOT_BASE_URL}
        status={isError ? 'error' : 'unknown'}
        isLoading={isLoading}
        isError={isError}
        errorMessage={mapFetchError(error)}
        onRetry={() => refetch()}
        isRetrying={isFetching}
      />
    )
  }

  if (!data) return null

  // Map seedbot's status vocabulary to ServiceStatus
  let status: ServiceStatus
  if (data.status === 'healthy') status = 'ok'
  else if (data.status === 'degraded') status = 'degraded'
  else status = 'unknown' // 'initializing' or anything else

  // Top-level sub-metrics row (the at-a-glance summary)
  const subMetrics: SubMetric[] = [
    { label: 'Uptime', value: fmtUptime(data.uptime) },
    { label: 'Scheduler', value: data.scheduler.running ? 'running' : 'stopped' },
    { label: 'Cadence', value: getSeedbotCadence(data.scheduler) },
    { label: 'Success rate', value: data.stats.successRate ?? '—' },
  ]

  const wallets = data.wallets ?? []
  const lowWalletCount = wallets.filter(w => w.isLow).length
  const trendingActors = data.trendingPipeline?.actors ?? []
  const lastConcept = data.trendingPipeline?.lastAIConcept

  return (
    <ServiceHealthCard
      title="seedbot"
      url={SEEDBOT_BASE_URL}
      status={status}
      statusLabel={data.status}
      subMetrics={subMetrics}
    >
      {/* 5 sub-cards inside */}
      <div className="mt-2 space-y-4">
        {/* Wallets */}
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Wallets ({wallets.length})
            </p>
            {lowWalletCount > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-status-error">
                <AlertTriangle className="h-3 w-3" />
                {lowWalletCount} low
              </span>
            )}
          </div>
          {wallets.length === 0 ? (
            <p className="text-xs text-muted-foreground">No wallets configured.</p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block rounded-md border bg-background/50 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Address
                      </th>
                      <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Balance (SOL)
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-16">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {wallets.map(w => (
                      <tr
                        key={w.address}
                        className={cn(
                          'border-b border-border last:border-0',
                          w.isLow && 'border-l-2 border-l-status-error'
                        )}
                      >
                        <td className="px-3 py-1.5">
                          <button
                            type="button"
                            onClick={() => copy(w.address, 'wallet address')}
                            className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors"
                            title={w.address}
                          >
                            <span>{truncateAddress(w.address)}</span>
                            <Copy className="h-3 w-3" />
                          </button>
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs font-mono">
                          {w.balanceSol}
                        </td>
                        <td className="px-3 py-1.5">
                          {w.isLow ? (
                            <span className="inline-flex items-center gap-1 text-xs text-status-error">
                              <AlertTriangle className="h-3 w-3" /> low
                            </span>
                          ) : (
                            <span className="text-xs text-status-ok">ok</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="md:hidden space-y-2">
                {wallets.map(w => (
                  <div
                    key={w.address}
                    className={cn(
                      'rounded-md border bg-background/50 p-3 space-y-1',
                      w.isLow && 'border-l-2 border-l-status-error'
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => copy(w.address, 'wallet address')}
                        className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors"
                        title={w.address}
                      >
                        <span>{truncateAddress(w.address)}</span>
                        <Copy className="h-3 w-3" />
                      </button>
                      {w.isLow ? (
                        <span className="inline-flex items-center gap-1 text-xs text-status-error">
                          <AlertTriangle className="h-3 w-3" /> low
                        </span>
                      ) : (
                        <span className="text-xs text-status-ok">ok</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Balance</span>
                      <span className="font-mono">{w.balanceSol} SOL</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Content library + Launch stats side-by-side */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
          <div className="rounded-md border bg-background/50 p-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Content Library
            </p>
            <p className="text-lg font-bold">
              {(data.contentLibrary?.totalConcepts ?? 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">concepts available</p>
            {lastConcept && (
              <p className="text-xs text-muted-foreground mt-2 truncate" title={lastConcept.name}>
                Last: <span className="text-foreground">{lastConcept.name}</span>{' '}
                <span className="font-mono">${lastConcept.ticker}</span>
              </p>
            )}
          </div>

          <div className="rounded-md border bg-background/50 p-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Launch Stats
            </p>
            <p className="text-lg font-bold">{data.stats.successRate}</p>
            <p className="text-xs text-muted-foreground">
              {data.stats.totalSuccesses.toLocaleString()} ok ·{' '}
              <span className="text-status-error">{data.stats.totalFailures.toLocaleString()} failed</span> ·{' '}
              {data.stats.totalAttempts.toLocaleString()} total
            </p>
          </div>
        </div>

        {/* Trending pipeline */}
        <div className="rounded-md border bg-background/50 p-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Trending Pipeline
          </p>
          <p className="text-xs text-muted-foreground">
            {(data.trendingPipeline?.cachedTopicCount ?? 0).toLocaleString()} cached topics ·{' '}
            {trendingActors.length} actor{trendingActors.length !== 1 ? 's' : ''}
          </p>
          {trendingActors.length > 0 && (
            <ul className="mt-2 space-y-1">
              {trendingActors.map(a => (
                <li key={a.id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate">{a.name}</span>
                  <span className="text-muted-foreground font-mono uppercase text-[10px] px-1.5 py-0.5 rounded bg-muted">
                    {a.platform}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ServiceHealthCard>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Main section
// ────────────────────────────────────────────────────────────────────────

export function BackendServicesSection() {
  return (
    <div>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
          <Server className="h-5 w-5 text-brand-fg" />
          Backend Services
        </h3>
        <RealTimeIndicator />
      </div>

      <div className="space-y-4">
        <MarketDataGroup />
        <MarketMakerGroup />
        <SeedbotGroup />
      </div>
    </div>
  )
}
